import ChatService from "../store/services/ChatService";
import { normalizeChatGroupSelectedForViewport } from "./messengerViewport";
/** Thành viên được thêm người: admin / join_policy=2; official group chỉ khi API cho phép (chat_access=public). */
export function canAddChatMember(group, viewer) {
    if (!group)
        return false;
    if (group.is_official_team_group === true) {
        if (group.can_manual_add_members === true)
            return true;
        if (group.can_manual_add_members === false)
            return false;
        return String(viewer?.chat_access || "") === "public";
    }
    if (Number(group.is_admin) === 1)
        return true;
    return Number(group.settings?.join_policy ?? 1) === 2;
}
export function withJoinPolicy(group, joinPolicy) {
    const settings = { ...(group.settings || {}), join_policy: joinPolicy };
    return { ...group, settings };
}
/** Trích `group_id` từ link chia sẻ `/messenger/{id}` (mọi domain hợp lệ). */
/** Vuex persist: cache theo group_id phải là object, không phải array. */
export { coerceMessageCacheMap } from "./chatMemory";
export function parseMessengerGroupIdFromLink(link) {
    const trimmed = String(link || "").trim();
    if (!trimmed)
        return null;
    const tryPath = (pathname) => {
        const match = pathname.match(/\/messenger\/(\d+)\/?$/);
        if (!match)
            return null;
        const id = parseInt(match[1], 10);
        return Number.isFinite(id) && id > 0 ? id : null;
    };
    try {
        const base = typeof window !== "undefined" ? window.location.origin : "https://localhost";
        const url = new URL(trimmed, base);
        return tryPath(url.pathname);
    }
    catch {
        return tryPath(trimmed.startsWith("/") ? trimmed : `/${trimmed}`);
    }
}
/** Tránh trùng id khi merge load-more / fetch theo group_id. */
export function dedupeChatGroupsById(groups) {
    if (!Array.isArray(groups))
        return [];
    const seen = new Set();
    const out = [];
    for (let i = 0; i < groups.length; i += 1) {
        const g = groups[i];
        if (!g)
            continue;
        const id = g.id;
        if (id == null) {
            out.push(g);
            continue;
        }
        const key = String(id);
        if (seen.has(key))
            continue;
        seen.add(key);
        out.push(g);
    }
    return out;
}
function chatGroupRecentTimestamp(row) {
    const raw = row?.last_message?.created_at || row?.created_at || 0;
    const ts = new Date(raw).getTime();
    return Number.isFinite(ts) ? ts : 0;
}
/** Sidebar: ghim trước, còn lại sắp theo tin nhắn gần nhất. */
export function sortChatGroupsForSidebar(groups) {
    if (!Array.isArray(groups) || groups.length <= 1) {
        return Array.isArray(groups) ? [...groups] : [];
    }
    return [...groups].sort((a, b) => {
        const aPinned = !!a.pin_at;
        const bPinned = !!b.pin_at;
        if (aPinned && bPinned) {
            const ap = new Date(String(a.pin_at)).getTime();
            const bp = new Date(String(b.pin_at)).getTime();
            return (Number.isFinite(bp) ? bp : 0) - (Number.isFinite(ap) ? ap : 0);
        }
        if (aPinned !== bPinned) {
            return aPinned ? -1 : 1;
        }
        return chatGroupRecentTimestamp(b) - chatGroupRecentTimestamp(a);
    });
}
/**
 * Đảm bảo nhóm chat có trong store (fetch theo group_id nếu chưa có trong trang 1).
 * Tránh redirect nhầm khi user có nhiều hội thoại / F5 deep link.
 */
async function fetchChatGroupRow(groupId) {
    try {
        const res = (await ChatService.getChatGroups({
            page: 1,
            per_page: 1,
            group_id: groupId,
        }));
        return res?.data?.[0] ?? null;
    }
    catch {
        return null;
    }
}
/**
 * F5 / deep link: load lần lượt page 1 → N cho đến khi gặp `activeGroupId`,
 * giữ đúng thứ tự sidebar (không prepend nhóm đang mở lên đầu).
 */
export async function loadChatGroupsThroughActiveGroup(activeGroupId, perPage = 20, extraParams = {}) {
    const gid = String(activeGroupId);
    let page = 1;
    let lastPage = 1;
    let all = [];
    let found = false;
    while (page <= 40) {
        const res = (await ChatService.getChatGroups({
            page,
            per_page: perPage,
            ...extraParams,
        }));
        lastPage = Math.max(1, Number(res?.meta?.last_page ?? 1));
        const chunk = Array.isArray(res?.data) ? res.data : [];
        all = dedupeChatGroupsById(all.concat(chunk));
        if (chunk.some((g) => g && String(g.id) === gid)) {
            found = true;
            break;
        }
        if (page >= lastPage) {
            break;
        }
        page += 1;
    }
    return {
        groups: all.slice(0, 120),
        page,
        lastPage,
        found,
    };
}
/** Giữ nhóm đang mở (theo URL / selected) khi refresh danh sách trang 1. */
export function mergeChatGroupsPreservingActive(incoming, store, activeGroupId) {
    const base = dedupeChatGroupsById(incoming);
    const gid = activeGroupId != null && String(activeGroupId) !== ""
        ? String(activeGroupId)
        : String(store.state.chat?.chatGroupSelected?.id ?? "");
    if (!gid || /^new\d+$/i.test(gid))
        return base;
    const fromList = base.find((g) => String(g.id) === gid);
    if (fromList)
        return base;
    const selected = store.state.chat?.chatGroupSelected;
    if (selected && String(selected.id) === gid) {
        return dedupeChatGroupsById([selected, ...base]);
    }
    return base;
}
export async function ensureChatGroupInStore(store, groupId, options) {
    const gid = groupId != null ? String(groupId) : "";
    if (!gid || /^new\d+$/i.test(gid))
        return null;
    const groups = store.state.chat?.chatGroups || [];
    const existing = groups.find((g) => String(g.id) === gid);
    if (existing)
        return existing;
    let row = await fetchChatGroupRow(gid);
    if (!row && options?.retry !== false) {
        await new Promise((r) => setTimeout(r, 400));
        row = await fetchChatGroupRow(gid);
    }
    if (!row)
        return null;
    const merged = dedupeChatGroupsById([row, ...groups]);
    store.commit("chat/updateChatGroups", merged);
    return row;
}
/** Đồng bộ `chatGroupSelected` với `messenger_id` trên URL (F5 / deep link). */
export async function syncMessengerRouteGroup(store, routeGroupId) {
    const gid = routeGroupId != null ? String(routeGroupId) : "";
    if (!gid || /^new\d+$/i.test(gid))
        return null;
    const row = await ensureChatGroupInStore(store, gid);
    if (!row)
        return null;
    let next = { ...row };
    delete next.isCreateChat;
    delete next.create_new_message;
    next = normalizeChatGroupSelectedForViewport(next);
    store.commit("chat/updateChatGroupSelected", next);
    return row;
}
